import { Box, Button, Portal, Text, HStack, VStack, Icon, useBreakpointValue, Table, Tbody, Tr, Td } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FaFastBackward, FaStepBackward, FaPlay, FaStepForward, FaFastForward, FaEdit, FaMapMarker, FaEyeSlash, FaVolumeDown, FaTag } from 'react-icons/fa';
import { FiShare2, FiDownload, FiInfo, FiPlus } from 'react-icons/fi';
import { TbRepeat } from 'react-icons/tb';

interface TooltipProps {
  text: string;
  elementSelectors: string[];
  currentStep: number;
  onBack: () => void;
  onNext: () => void;
  onDismiss: () => void;
  onDontShowAgain: () => void;
  isFirstStep: boolean;
  isMobile?: boolean;
  customIcon1?: React.ReactNode;
  customIcon2?: React.ReactNode;
  customIcon3?: React.ReactNode;
}

interface SpotlightRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

const KeyBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    px={2}
    py={0.5}
    bg="whiteAlpha.200"
    borderRadius="md"
    fontSize="sm"
    border="1px solid"
    borderColor="whiteAlpha.300"
    display="inline-block"
  >
    {children}
  </Box>
);

// Mono/Stereo Icon Component for tutorial (shows stereo - two overlapping circles)
const MonoIconTutorial = ({ size = 20 }: { size?: number }) => {
  const iconSize = size;
  const strokeWidth = 2;
  
  return (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Left circle */}
      <circle
        cx="14"
        cy="20"
        r="12"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Right circle */}
      <circle
        cx="26"
        cy="20"
        r="12"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
      />
    </svg>
  );
};

// Filter Icon Component for tutorial
const FilterIconTutorial = ({ size = 20 }: { size?: number }) => {
  const iconSize = size;
  // Match stereo icon stroke width visually
  // Stereo icon uses strokeWidth=2 in 24x24 viewBox
  // Using a slightly thicker stroke width
  const strokeWidth = 22;
  
  return (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 411.5 411.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(205.75, 205.75) scale(0.85) translate(-205.75, -205.75)">
        <path d="M214.04,0l17.18,1.57c90.11,10.85,163.77,82.08,177.7,171.76l2.58,24.17v16.5l-1.56,17.19c-10.92,92.51-86.35,167.72-178.71,178.75l-17.18,1.56h-16.5l-17.18-1.56C87.76,398.78,12.75,323.87,1.65,231.18L.08,214.5c.19-5.81-.26-11.7,0-17.5C4.92,90.23,90.87,4.66,197.54,0h16.5ZM391.5,163C374.6,83.36,303.26,22.12,222.28,15.5,60.9,2.31-43.99,183.47,50.17,316.16c1.36,1.91,9.18,12.81,10.65,12.85l72.91-163.84c.48-.47,3.63-2.17,4.08-2.17h253.7ZM143.61,178.06l-71.87,163.2c28.75,29.08,67.91,48.31,108.63,53.67,118.3,15.56,221.31-79.19,216.16-198.2l-2.03-19.23-250.89.56Z"/>
      </g>
    </svg>
  );
};

// Dim Icon Component for tutorial (volume down icon)
const DimIconTutorial = ({ size = 20 }: { size?: number }) => {
  return <Icon as={FaVolumeDown} boxSize={`${size}px`} color="currentColor" />;
};

const steps = [
  {
    text: `Player Switching\n\nEach player contains one of your audio files.\n\n<customIcon> Cycle selection\n\n<key>/</key> Random\n\n<key>↑</key>/<key>↓</key> Switch up/down in the player list\n\n<key>⌘↑</key> / <key>⌘↓</key> (or drag) to reorder players in the list\n\nYou can also click a player, or press numbers 1-9 to select it.`,
    elementSelectors: [
      'div[data-player-number="1"]',
      '[data-testid="player-switch-button"]'
    ],
    customIcon: (
      <Box
        width="24px"
        height="24px"
        borderRadius="full"
        border="2px solid"
        borderColor="accent.500"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="accent.400"
        fontSize="md"
        fontWeight="bold"
      >
        1
      </Box>
    ),
    randomIcon: (
      <Box
        width="24px"
        height="24px"
        minWidth="24px"
        minHeight="24px"
        maxWidth="24px"
        maxHeight="24px"
        borderRadius="full"
        border="2px solid"
        borderColor="accent.500"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="accent.400"
        fontSize="md"
        fontWeight="bold"
        lineHeight="1"
        overflow="hidden"
      >
        ?
      </Box>
    )
  },
  {
    text: "Transport Controls\n\n<transportTable>",
    elementSelectors: [
      '[data-testid="rewind-10-button"]',
      '[data-testid="rewind-3-button"]',
      '[data-testid="play-pause-button"]',
      '[data-testid="forward-3-button"]',
      '[data-testid="forward-10-button"]'
    ],
    transportControls: [
      { icon: FaFastBackward, keys: "⌘←", description: "Return to 0:00", isHold: true },
      { icon: FaFastBackward, keys: "⇧←", description: "Back 10sec" },
      { icon: FaStepBackward, keys: "←", description: "Back 3sec" },
      { icon: FaPlay, keys: "Space", description: "Play/pause" },
      { icon: FaStepForward, keys: "→", description: "Forward 3sec" },
      { icon: FaFastForward, keys: "⇧→", description: "Forward 10sec" },
      { icon: null, keys: "⌥←", description: "Replay from last position" },
      { icon: null, keys: "⌃⌥←", description: "Replay and switch player (2 players only)" }
    ]
  },
  {
    text: "Monitoring\n\n<transportTable>",
    elementSelectors: [
      '[data-testid="dim-button"]',
      '[data-testid="stereo-mode-button"]',
      '[data-testid="filter-control-button"]'
    ],
    transportControls: [
      { customIcon: <DimIconTutorial size={20} />, keys: "D", description: "Dim master volume" },
      { customIcon: <MonoIconTutorial size={20} />, keys: "M", description: "Cycle stereo/mono/sides listening modes" },
      { customIcon: <FilterIconTutorial size={20} />, keys: "F", description: "Master filter controls (highpass, lowpass, bandpass)" },
      { keys: "F F", description: "Toggle current filter on/off (double-tap F). You can also double-click." }
    ]
  },
  {
    text: "<blindHeader>Blind Test Modes\n\nHotkey: <key>B</key>\n\n• Basic: simple switch-and-reveal.\n• Pick Fave: find out which track you really prefer — Compare (scores) or Narrow Down (knockout to a winner).\n• Guess Track: how good are your ears?\n\n<blindModeIcon> Each player can be removed from the Blind Mode testing pool with this toggle button",
    elementSelectors: [
      '[data-testid="blind-test-button"]',
      'div[data-player-number="1"] [aria-label="Toggle blind test inclusion"]'
    ],
    blindModeIcon: (
      <Icon as={FaEyeSlash} color="accent.400" boxSize="20px" />
    )
  },
  {
    text: "Compare sections at different points on the timeline.\n\nManual Sync\n\n<customIcon1> Set an anchor point for each track (e.g. beat 1 of the chorus).\n\n<customIcon2> Sync anchor points.\n\n<customIcon3> Press again to reset sync.\n\n\nAuto-Sync\n\n<customIcon3> Press without first setting anchor points (experimental!).",
    elementSelectors: [
      '[data-testid="sync-point-button"]',
      '[data-testid="jump-sync-button"]'
    ],
    customIcon1: (
      <Box position="relative" color="gray.400">
        <FaMapMarker size={24} />
        <Text
          position="absolute"
          top="42%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="14px"
          fontWeight="bold"
          color="black"
        >
          A
        </Text>
      </Box>
    ),
    customIcon2: (
      <Box position="relative" color="gray.300" opacity={0.8}>
        <FaMapMarker size={24} />
        <Text
          position="absolute"
          top="42%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="14px"
          fontWeight="bold"
          color="black"
        >
          S
        </Text>
      </Box>
    ),
    customIcon3: (
      <Box position="relative" color="accent.400">
        <FaMapMarker size={24} />
        <Text
          position="absolute"
          top="42%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="14px"
          fontWeight="bold"
          color="black"
        >
          S
        </Text>
      </Box>
    )
  },
  {
    text: "<regionHeader>Regions\n\n<key>R</key> Add a visual region bar below the waveform to denote different sections (verse/chorus, etc)\n\n<loopHeader>Looping\n\n<key>L</key> to set the loop start point.\n<key>L</key> again to set the loop end point.\n<key>L</key> once more to clear the loop.\n\nYou can also <key>⌥ drag</key> to set a loop region",
    elementSelectors: [
      '[data-testid="loop-button"]'
    ]
  },
  {
    text: "Import, Sharing & Notes\n\n<sharingIcons>",
    elementSelectors: [
      '[data-testid="share-button"]',
      'div[data-player-number="1"] [data-testid="notes-button"]',
      '[data-testid="download-button"]',
      '[data-testid="add-files-button"]'
    ],
    sharingItems: [
      { 
        icon: FiPlus, 
        color: "blue.400", 
        description: "Import additional files. Hotkey: <key>I</key>" 
      },
      { 
        icon: FiShare2, 
        color: "blue.400", 
        description: "If you load files from Dropbox or Google Drive, they can be shared with friends." 
      },
      { 
        icon: FaEdit, 
        color: "blue.400", 
        description: "Edit track notes (<key>N</key>) and add timestamps (<key>⌘I</key>) that will be included in your shared setups." 
      },
      { 
        icon: FiDownload, 
        color: "blue.400", 
        description: "If the \"Allow Downloads\" button is checked during setup sharing, all files can be downloaded." 
      }
    ]
  }
];

const Tooltip = ({ text, elementSelectors, currentStep, onBack, onNext, onDismiss, onDontShowAgain, isFirstStep, isMobile, customIcon1, customIcon2, customIcon3 }: TooltipProps) => {
  const [spotlightRects, setSpotlightRects] = useState<SpotlightRect[]>([]);

  useEffect(() => {
    console.log('Tooltip effect running with selectors:', elementSelectors);
    const updateSpotlightPositions = () => {
      const rects: SpotlightRect[] = [];
      
      elementSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        console.log(`Found elements for selector ${selector}:`, elements.length);
        
        if (elements.length > 0) {
          // Get the first element's rect as starting point
          const firstRect = elements[0].getBoundingClientRect();
          console.log(`First element rect for ${selector}:`, firstRect);
          
          let minLeft = firstRect.left;
          let minTop = firstRect.top;
          let maxRight = firstRect.right;
          let maxBottom = firstRect.bottom;

          // Find the extremes of all element rects
          elements.forEach(element => {
            const rect = element.getBoundingClientRect();
            minLeft = Math.min(minLeft, rect.left);
            minTop = Math.min(minTop, rect.top);
            maxRight = Math.max(maxRight, rect.right);
            maxBottom = Math.max(maxBottom, rect.bottom);
          });

          // Add padding around the combined rectangle
          rects.push({
            top: minTop - 8,
            left: minLeft - 8,
            right: maxRight + 8,
            bottom: maxBottom + 8,
            width: maxRight - minLeft + 16,
            height: maxBottom - minTop + 16
          });
        }
      });

      // Merge overlapping or adjacent rectangles
      const mergeRects = (rectangles: SpotlightRect[]): SpotlightRect[] => {
        if (rectangles.length <= 1) return rectangles;
        
        const threshold = 20; // pixels - merge if rectangles are within this distance
        let merged = [...rectangles];
        let changed = true;
        
        // Keep merging until no more merges are possible
        while (changed && merged.length > 1) {
          changed = false;
          const newMerged: SpotlightRect[] = [];
          const used = new Set<number>();
          
          for (let i = 0; i < merged.length; i++) {
            if (used.has(i)) continue;
            
            let combined: SpotlightRect = { ...merged[i] };
            used.add(i);
            
            // Try to merge with all other rectangles
            for (let j = i + 1; j < merged.length; j++) {
              if (used.has(j)) continue;
              
              const other = merged[j];
              
              // Check if rectangles overlap or are close together
              const horizontalOverlap = !(combined.right + threshold < other.left || other.right + threshold < combined.left);
              const verticalOverlap = !(combined.bottom + threshold < other.top || other.bottom + threshold < combined.top);
              
              if (horizontalOverlap && verticalOverlap) {
                // Merge the rectangles
                combined = {
                  top: Math.min(combined.top, other.top),
                  left: Math.min(combined.left, other.left),
                  right: Math.max(combined.right, other.right),
                  bottom: Math.max(combined.bottom, other.bottom),
                  width: Math.max(combined.right, other.right) - Math.min(combined.left, other.left),
                  height: Math.max(combined.bottom, other.bottom) - Math.min(combined.top, other.top)
                };
                used.add(j);
                changed = true;
              }
            }
            
            newMerged.push(combined);
          }
          
          merged = newMerged;
        }
        
        return merged;
      };

      const mergedRects = mergeRects(rects);
      setSpotlightRects(mergedRects);
    };

    updateSpotlightPositions();
    window.addEventListener('resize', updateSpotlightPositions);
    return () => window.removeEventListener('resize', updateSpotlightPositions);
  }, [elementSelectors]);

  if (spotlightRects.length === 0) return null;

  return (
    <>
      {/* Dark overlay with cutouts */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        minH="780px"
        minW="780px"
        bg="rgba(0, 0, 0, 0.6)"
        backdropFilter="blur(2px)"
        zIndex={999}
        pointerEvents="none"
        sx={{
          clipPath: `path('M 0 0 L 0 ${Math.max(window.innerHeight, 780)} L ${Math.max(window.innerWidth, 780)} ${Math.max(window.innerHeight, 780)} L ${Math.max(window.innerWidth, 780)} 0 L 0 0 Z ${spotlightRects.map(rect => 
            `M ${rect.left} ${rect.top} L ${rect.right} ${rect.top} L ${rect.right} ${rect.bottom} L ${rect.left} ${rect.bottom} Z`
          ).join(' ')}')`
        }}
      />

      {/* Spotlight borders */}
      {spotlightRects.map((rect, index) => (
        <Box
          key={index}
          position="absolute"
          top={`${rect.top}px`}
          left={`${rect.left}px`}
          width={`${rect.width}px`}
          height={`${rect.height}px`}
          border="2px solid rgba(255, 255, 255, 0.3)"
          borderRadius="md"
          zIndex={1000}
          pointerEvents="none"
          boxShadow="0 0 0 4px rgba(255, 255, 255, 0.1)"
        />
      ))}

      {/* Centered tooltip box */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="rgba(0, 0, 0, 0.8)"
        color="white"
        borderRadius="xl"
        boxShadow="dark-lg"
        fontSize="sm"
        w="600px"
        h="465px"
        textAlign="left"
        zIndex={1001}
        border="1px solid rgba(255, 165, 0, 0.5)"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Content area */}
        <Box 
          flex="1" 
          py={6} 
          px={8}
          overflowY="auto"
          bg="gray.900"
          borderTopLeftRadius="xl"
          borderTopRightRadius="xl"
          sx={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bg: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bg: 'whiteAlpha.200',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              bg: 'whiteAlpha.300',
            },
          }}
        >
          <Text fontSize="md" fontWeight="medium">
            <VStack spacing={3} align="start" w="100%">
              {text.split('\n\n').map((paragraph, i) => (
                <Text key={i} w="100%" whiteSpace="pre-wrap">
                  {paragraph.split('\n').map((line, j) => {
                    // Handle special headers with icons (can appear anywhere)
                    if (line.includes('<blindHeader>')) {
                      const headerText = line.replace('<blindHeader>', '');
                      return (
                        <HStack key={j} spacing={2} mb={3} alignItems="center">
                          <Icon as={FaEyeSlash} boxSize="24px" color="white" />
                          <Text fontSize="xl" fontWeight="bold">
                            {headerText}
                          </Text>
                        </HStack>
                      );
                    }
                    if (line.includes('<loopHeader>')) {
                      const headerText = line.replace('<loopHeader>', '');
                      return (
                        <HStack key={j} spacing={2} mb={3} alignItems="center">
                          <Icon as={TbRepeat} boxSize="24px" color="white" />
                          <Text fontSize="xl" fontWeight="bold">
                            {headerText}
                          </Text>
                        </HStack>
                      );
                    }
                    if (line.includes('<regionHeader>')) {
                      const headerText = line.replace('<regionHeader>', '');
                      return (
                        <Text key={j} fontSize="xl" fontWeight="bold" mb={3}>
                          {headerText}
                        </Text>
                      );
                    }
                    // Check if it's a header (first line of a section)
                    if (j === 0 && i === 0) {
                      // Handle conditional import header
                      if (line.includes('Import, Sharing & Notes')) {
                        const hasImportButton = document.querySelector('[data-testid="add-files-button"]') !== null;
                        const displayTitle = hasImportButton ? 'Import, Sharing & Notes' : 'Sharing & Notes';
                        return (
                          <Text key={j} fontSize="xl" fontWeight="bold" mb={3}>
                            {displayTitle}
                          </Text>
                        );
                      }
                      return (
                        <Text key={j} fontSize="xl" fontWeight="bold" mb={3}>
                          {line}
                        </Text>
                      );
                    }

                    // Handle custom icon
                    if (line.includes('<customIcon>')) {
                      const [before, after] = line.split('<customIcon>');
                      return (
                        <Box key={j} display="flex" alignItems="center" gap={2} mb={2}>
                          <Text>{before}</Text>
                          {steps[currentStep].customIcon}
                          <Text>{after}</Text>
                        </Box>
                      );
                    }



                    // Handle transport table
                    if (line.includes('<transportTable>')) {
                      return (
                        <Box key={j} w="100%" mb={3}>
                          <Table variant="simple" size="sm">
                            <Tbody>
                              {steps[currentStep].transportControls?.map((control, idx) => (
                                <Tr key={idx} borderBottom="none">
                                  <Td p={1} w="60px" borderBottom="none">
                                    <HStack spacing={1}>
                                      {'customIcon' in control && control.customIcon ? (
                                        <Box color="white" width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                                          {control.customIcon}
                                        </Box>
                                      ) : 'icon' in control && control.icon ? (
                                        <Icon as={control.icon} boxSize="20px" color="white" />
                                      ) : null}
                                      {'isHold' in control && control.isHold && <Text color="white" fontSize="xs">(hold)</Text>}
                                    </HStack>
                                  </Td>
                                  <Td p={1} w="80px" borderBottom="none">
                                    <KeyBox>{control.keys}</KeyBox>
                                  </Td>
                                  <Td p={1} color="white" borderBottom="none">
                                    {control.description}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      );
                    }

                    // Handle sharing icons
                    if (line.includes('<sharingIcons>')) {
                      const hasImportButton = document.querySelector('[data-testid="add-files-button"]') !== null;
                      const filteredItems = steps[currentStep].sharingItems?.filter((item, idx) => {
                        // First item is the import item, only show it if import button exists
                        if (idx === 0 && item.icon === FiPlus) {
                          return hasImportButton;
                        }
                        return true;
                      });
                      
                      return (
                        <Box key={j} w="100%">
                          <VStack spacing={2} align="start">
                            {filteredItems?.map((item, idx) => (
                              <HStack key={idx} spacing={3} align="start">
                                <Icon as={item.icon} boxSize="24px" color={item.color} />
                                <Text color="white" flex="1">
                                  {item.description.split(/<key>|<\/key>/).map((part, k) => {
                                    if (k % 2 === 1) { // This is a key
                                      return !isMobile ? <KeyBox key={k}>{part}</KeyBox> : null;
                                    }
                                    return part;
                                  })}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      );
                    }

                    // Handle random icon
                    if (line.includes('<randomIcon>')) {
                      const [before, after] = line.split('<randomIcon>');
                      return (
                        <Box key={j} display="flex" alignItems="center" gap={2} mb={2}>
                          <Text>{before}</Text>
                          {steps[currentStep].randomIcon}
                          <Text>{after}</Text>
                        </Box>
                      );
                    }

                    // Handle blind mode icon
                    if (line.includes('<blindModeIcon>')) {
                      const [before, after] = line.split('<blindModeIcon>');
                      return (
                        <Box key={j} display="flex" alignItems="center" gap={2} mb={2}>
                          <Text>{before}</Text>
                          {steps[currentStep].blindModeIcon}
                          <Text>{after}</Text>
                        </Box>
                      );
                    }

                    // Handle custom icons
                    if (line.includes('<customIcon1>')) {
                      const [before, after] = line.split('<customIcon1>');
                      return (
                        <Box key={j} display="flex" alignItems="center" gap={2} mb={2}>
                          <Text>{before}</Text>
                          {customIcon1}
                          <Text>{after}</Text>
                        </Box>
                      );
                    }
                    if (line.includes('<customIcon2>')) {
                      const [before, after] = line.split('<customIcon2>');
                      return (
                        <Box key={j} display="flex" alignItems="center" gap={2} mb={2}>
                          <Text>{before}</Text>
                          {customIcon2}
                          <Text>{after}</Text>
                        </Box>
                      );
                    }
                    if (line.includes('<customIcon3>')) {
                      const [before, after] = line.split('<customIcon3>');
                      return (
                        <Box key={j} display="flex" alignItems="center" gap={2} mb={2}>
                          <Text>{before}</Text>
                          {customIcon3}
                          <Text>{after}</Text>
                        </Box>
                      );
                    }

                    // Handle icons
                    if (line.includes('<Icon')) {
                      const parts = line.split(/<Icon as=\{|\} \/>/);
                      return (
                        <HStack key={j} spacing={2} mb={2} alignItems="center">
                          {parts.map((part, k) => {
                            if (part.startsWith('Fa') || part.startsWith('Fi')) {
                              const IconComponent = {
                                FaFastBackward,
                                FaStepBackward,
                                FaPlay,
                                FaStepForward,
                                FaFastForward,
                                FaEdit,
                                FiShare2,
                                FiDownload
                              }[part];
                              // Make icons bigger for the sharing & notes section
                              const isShareSection = text.startsWith("Sharing & Notes");
                              return <Icon 
                                key={k} 
                                as={IconComponent} 
                                boxSize={isShareSection ? "24px" : undefined}
                              />;
                            }
                            // Handle key tags in the text
                            if (part.includes('<key>')) {
                              const keyParts = part.split(/<key>|<\/key>/);
                              return (
                                <Text key={k} as="span">
                                  {keyParts.map((keyPart, l) => {
                                    if (l % 2 === 1) { // This is a key
                                      return <KeyBox key={l}>{keyPart}</KeyBox>;
                                    }
                                    return keyPart;
                                  })}
                                </Text>
                              );
                            }
                            return <Text key={k}>{part}</Text>;
                          })}
                        </HStack>
                      );
                    }

                    // Handle bullet points with proper indentation
                    if (line.startsWith('•')) {
                      return (
                        <Text key={j} mb={1} pl={4} textIndent="-1em" ml={4}>
                          {line.split(/<key>|<\/key>/).map((part, k) => {
                            if (k % 2 === 1) { // This is a key
                              return <KeyBox key={k}>{part}</KeyBox>;
                            }
                            return part;
                          })}
                        </Text>
                      );
                    }

                    // Handle subheadings
                    if (line === "Manual Sync" || line === "Auto-Sync") {
                      return (
                        <Text key={j} mb={2} mt={3} fontWeight="bold" fontSize="md">
                          {line}
                        </Text>
                      );
                    }

                    // Handle regular text with key tags and italic tags
                    const parts = [];
                    let remaining = line;
                    let keyIndex = 0;
                    let italicIndex = 0;
                    
                    while (remaining.length > 0) {
                      const keyMatch = remaining.match(/<key>(.*?)<\/key>/);
                      const italicMatch = remaining.match(/<italic>(.*?)<\/italic>/);
                      
                      let match = null;
                      let matchType = null;
                      
                      if (keyMatch && italicMatch) {
                        // Use whichever comes first
                        if (keyMatch.index! < italicMatch.index!) {
                          match = keyMatch;
                          matchType = 'key';
                        } else {
                          match = italicMatch;
                          matchType = 'italic';
                        }
                      } else if (keyMatch) {
                        match = keyMatch;
                        matchType = 'key';
                      } else if (italicMatch) {
                        match = italicMatch;
                        matchType = 'italic';
                      }
                      
                      if (match) {
                        // Add text before the match
                        if (match.index! > 0) {
                          parts.push(remaining.substring(0, match.index!));
                        }
                        // Add the matched content with appropriate formatting
                        if (matchType === 'key') {
                          parts.push(<KeyBox key={`key-${keyIndex++}`}>{match[1]}</KeyBox>);
                        } else {
                          parts.push(<Text as="span" key={`italic-${italicIndex++}`} fontStyle="italic">{match[1]}</Text>);
                        }
                        // Continue with remaining text
                        remaining = remaining.substring(match.index! + match[0].length);
                      } else {
                        // No more matches, add remaining text
                        parts.push(remaining);
                        remaining = '';
                      }
                    }
                    
                    return (
                      <Text key={j} mb={1}>
                        {parts}
                      </Text>
                    );
                  })}
                </Text>
              ))}
            </VStack>
          </Text>
        </Box>

        {/* Buttons area */}
        <Box 
          borderTop="1px" 
          borderColor="whiteAlpha.200"
          py={4} 
          px={6}
          bg="gray.900"
          borderBottomLeftRadius="xl"
          borderBottomRightRadius="xl"
        >
          <VStack spacing={3}>
            <HStack spacing={3} justify="space-between" w="100%">
              <Text color="whiteAlpha.700" fontSize="sm">
                {currentStep + 1}/{steps.length}
              </Text>
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={onBack}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="orange.500"
                  isDisabled={isFirstStep}
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={onNext}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="orange.500"
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={onDismiss}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="#ACD5F3"
                >
                  Let&apos;s go!
                </Button>
                <Button
                  variant="outline"
                  color="white"
                  size="sm"
                  onClick={onDontShowAgain}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  fontSize="sm"
                  fontWeight="medium"
                  borderColor="#ACD5F3"
                >
                  Don&apos;t show again
                </Button>
              </HStack>
            </HStack>
            <HStack spacing={2} color="whiteAlpha.600" fontSize="sm">
              <Icon as={FiInfo} boxSize="16px" />
              <Text>You can access this tutorial again via the info button</Text>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </>
  );
};

interface InfoOverlayProps {
  onDismiss: () => void;
  onDontShowAgain: () => void;
  hasLoadedFiles: boolean;
  readyPlayers: Set<number>;
  force?: boolean;
  onShown?: () => void;
}

const InfoOverlay = ({ onDismiss, onDontShowAgain, hasLoadedFiles, readyPlayers, force = false, onShown }: InfoOverlayProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasShownBefore, setHasShownBefore] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (force) {
      setHasShownBefore(false);
      setCurrentStep(0);
    }
  }, [force]);

  useEffect(() => {
    // Don't show on mobile devices
    if (isMobile) {
      setIsVisible(false);
      return;
    }
    
    const hasShown = localStorage.getItem('infoOverlayShown');
    
    // Don't show if already shown and not forced
    if (!force && hasShown === 'true') {
      setIsVisible(false);
      return;
    }

    // Only show when we have loaded files AND the first player is ready AND we haven't shown before
    if (hasLoadedFiles && readyPlayers.has(1) && (!hasShownBefore || force)) {
      let retryCount = 0;
      const maxRetries = 10; // Maximum number of retries
      const retryInterval = 100; // Time between retries in ms

      const checkForElements = () => {
        const playerElement = document.querySelector('div[data-player-number="1"]');
        const switchButton = document.querySelector('[data-testid="player-switch-button"]');
        
        if (playerElement && switchButton) {
          setIsVisible(true);
          if (!force) {
            setHasShownBefore(true);
          }
          onShown?.();
          return;
        }

        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkForElements, retryInterval);
        }
      };

      // Initial delay before starting to check
      setTimeout(checkForElements, 500);
    }
  }, [hasLoadedFiles, readyPlayers, hasShownBefore, force, onShown, isMobile]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('infoOverlayShown', 'true');
    setIsVisible(false);
    onDontShowAgain();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDismiss();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Portal>
      <Box 
        position="fixed" 
        inset={0} 
        zIndex={999} 
        overflow="auto"
        minH={{ base: "auto", md: "840px" }}
        minW={{ base: "auto", md: "780px" }}
        sx={{
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          '&::-webkit-scrollbar-track': {
            bg: 'rgba(0, 0, 0, 0.2)'
          },
          '&::-webkit-scrollbar-thumb': {
            bg: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px'
          }
        }}
      >
        <Box
          position="relative"
          w="100%"
          h="100%"
          minH={{ base: "auto", md: "840px" }}
          minW={{ base: "auto", md: "780px" }}
        >
          <Tooltip 
            {...steps[currentStep]} 
            currentStep={currentStep}
            onBack={handleBack}
            onNext={handleNext}
            onDismiss={handleDismiss}
            onDontShowAgain={handleDontShowAgain}
            isFirstStep={currentStep === 0}
            isMobile={isMobile}
            customIcon1={steps[currentStep].customIcon1}
            customIcon2={steps[currentStep].customIcon2}
            customIcon3={steps[currentStep].customIcon3}
          />
        </Box>
      </Box>
    </Portal>
  );
};

export default InfoOverlay; 